import { Navigate, Outlet } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import {
  selectAdminAuthority,
  selectCurrentUser,
  selectUserRoles
} from "../Users/usersSlice";
import { Role } from "../Auth/roles";
import { User } from "../../API";
import {
  NetworkFeatureFlags,
  selectAggregateFeatures,
  selectNetworkFeaturesState
} from "../Networks/networkFeaturesSlice";
import type { AdminAuthority } from "../Auth/adminAccess";

type ProtectedRouteAccessInput = {
  roles: Role[] | undefined;
  currentUser: User | undefined;
  /**
   * Per-network feature flags aggregated across every network the
   * current user has a program in. Populated by `useLoadNetworkFeatures`
   * in `Layout.tsx`; predicate routes wait for that load to settle so a
   * deep link isn't bounced before the flags land.
   */
  networkFeatures: NetworkFeatureFlags;
  /**
   * Coaching / Rotations authority from the caller's JWT claims, set during
   * bootstrap. Null means no Coaching / Rotations access.
   */
  adminAuthority: AdminAuthority | null;
};

type ProtectedRouteProps = {
  /**
   * If provided, the route is allowed when at least one of these roles
   * is present in `state.users.roles`. Used for the role-gated routes
   * (ProgramAdmin / SystemAdmin sections of the SPA).
   */
  requiredRoles?: Role[];
  /**
   * If provided, takes precedence over `requiredRoles` and is used as a
   * custom access check. Useful for gates that depend on data outside
   * the role list (e.g. "has an active Trainee or Attending program
   * membership"). Receives the same data the role check would have seen
   * so callers can mix-and-match.
   */
  requirePredicate?: (input: ProtectedRouteAccessInput) => boolean;
  children?: JSX.Element;
};

export const ProtectedRoute = ({
  requiredRoles,
  requirePredicate,
  children
}: ProtectedRouteProps) => {
  const roles = useAppSelector(selectUserRoles);
  const currentUser = useAppSelector(selectCurrentUser);
  const networkFeatures = useAppSelector(selectAggregateFeatures);
  const { status: networkFeaturesStatus } = useAppSelector(
    selectNetworkFeaturesState
  );
  const adminAuthority = useAppSelector(selectAdminAuthority);

  if (
    requirePredicate &&
    (networkFeaturesStatus === "idle" || networkFeaturesStatus === "loading")
  ) {
    return null;
  }

  const allowed = requirePredicate
    ? requirePredicate({ roles, currentUser, networkFeatures, adminAuthority })
    : !!roles?.some((role) => (requiredRoles ?? []).includes(role));

  if (!allowed) {
    return <Navigate to={"/"} replace />;
  }
  return children ? children : <Outlet />;
};
